import useStep3 from "./useStep3"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Badge, Button, Card, CardContent, Table, TableBody, TableHeader, TableRow } from "@/components/ui";
import { StepsProps } from "../types";
import { capitalize, cn } from "@/lib/utils";
import { Circle, CircleCheck, CircleDashed, UsersRound } from "lucide-react";
import { CustomTableCell, CustomTableHead, Loader } from "@/components";

const Step3 = ({onNextStepClick}: StepsProps) => {
  const {updatedRows, openTabs, onAccordionChange,
    onDiscard, onChangeCandid, isUploadPending,
    onImport, onMerge, disabledButton, onMergeAll, onDiscardAll, allActionUpdated
  } = useStep3({onNextStepClick});

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardContent className="text-[14px] text-gray-500 flex flex-col gap-5">
          <span>We found {updatedRows?.length} people who may already exist. Choose how to handle each — Merge updates the existing person and links this property. Discard skips the row. New creates a separate record.</span>
          <div className="flex flex-row items-center gap-5 justify-between p-3 bg-blue-50 rounded-md border-blue-200 border">
            <div className="flex flex-row items-center gap-2">
              <div className="bg-blue-200 p-2 rounded-md">
                <UsersRound className="stroke-blue-800"/>
              </div>
              <span className="text-[14px] text-black"><span className="font-semibold">Set all {updatedRows?.length} duplicates </span> at once, then fine-tune any individually below.</span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <Button type="button" onClick={onMergeAll} disabled={allActionUpdated}>Merge All</Button>
              <Button variant="outline" type="button" onClick={onDiscardAll} disabled={allActionUpdated}>Discard All</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Accordion
        type="multiple"
        value={openTabs}
        onValueChange={onAccordionChange}
        className="gap-4"
      >
        {updatedRows?.map((data: any, index: number) => {
          const userName = data?.owner?.split(" ");
          const selectedCandidate: any = (data as any)?.matchCandidates?.find((data: any) => data?.isSelected); 
          const propertyLinksMatchCandid = selectedCandidate?.propertyLinks?.map((data: any) => data?.propertyName)?.join(", ");

          const rowTableData = [
            {
              field: "Name",
              fromYourFile: data?.owner,
              matchCandid: selectedCandidate?.name,
              isSame: data?.owner == selectedCandidate?.name
            },
            {
              field: "Mobile",
              fromYourFile: data?.mobileNumber,
              matchCandid: selectedCandidate?.mobileNumber,
              isSame: data?.mobileNumber == selectedCandidate?.mobileNumber
            },
            {
              field: "Email",
              fromYourFile: data?.email,
              matchCandid: selectedCandidate?.email,
              isSame: data?.email == selectedCandidate?.email
            },
            {
              field: "Property Link",
              fromYourFile: data?.propertyName,
              matchCandid: propertyLinksMatchCandid,
              isSame: data?.propertyName == propertyLinksMatchCandid
            },
            {
              field: "Address",
              fromYourFile: data?.address,
              matchCandid: selectedCandidate?.address,
              isSame: data?.address == selectedCandidate?.address
            }
          ];

          return (
            <AccordionItem
              value={`${index + 1}`}
              className="bg-white px-5 rounded-lg mb-[20px] shadow-md"
            >
              <AccordionTrigger className="cursor-pointer">
                <div className="flex flex-row items-center justify-between w-full">
                  <div className="flex flex-row gap-4">
                    <div className="bg-primary h-[40px] w-[40px] text-[16px] font-semibold rounded-full text-white flex items-center justify-center">
                      <span>{capitalize(userName[0]?.charAt(0))}{capitalize(userName[userName.length - 1]?.charAt(0))}</span>
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <div className="flex flex-row items-start gap-2">
                        <span className="text-[14px]">{data?.owner || ""}</span>
                        <span className="text-[10px] bg-blue-50 text-primary px-2 rounded-full">From your file</span>
                      </div>
                      <div className="flex flex-row gap-2 text-[10px] text-gray-500">
                        <span>{data?.email}</span>
                        <span>{data?.phoneNo}</span>
                        <span>{data?.propertyName}</span>
                      </div>
                    </div>
                  </div>
                  {data?.action ? 
                    <Badge className={cn(data?.action == "discard" ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600", "rounded-full text-[14px]")}>
                      {capitalize(data?.action)}
                    </Badge> 
                    : <div className="flex flex-row gap-3 items-center">
                    <Button onClick={onMerge(index)}>
                      Merge
                    </Button>
                    <Button onClick={onDiscard(index)} variant="outline">
                      Discard
                    </Button>
                  </div>}
                </div>
              </AccordionTrigger>
              <AccordionContent className="border-t pt-3">
                <div className="grid grid-cols-1 lg:grid-cols-8">
                  <div className="lg:col-span-3 flex flex-col gap-2 lg:border-r lg:pr-2">
                    <div className="flex flex-col">
                      <span className="text-[13px]">Choose an existing record to link with</span>
                      <span className="text-[12px] text-gray-500">Select the best match from the options below</span>
                    </div>
                    <div className="flex flex-col gap-5">
                      {data?.matchCandidates?.map((candid: any, idx: string) => {
                        const candidName = candid?.name?.split(" ");
                        const isExactPhone = candid?.phoneNo == data?.phoneNo;
                        const isExactEmail = candid?.email == data?.email;
                        const isSelected = candid?.isSelected;

                        return (
                          <div key={`candid-${candid.userId}-${idx}`}
                            className={cn("border border-gray-200 p-2 rounded-md flex flex-col gap-3 cursor-pointer", isSelected ? "bg-blue-50" : "")}
                            onClick={() => onChangeCandid(candid?.userId)}
                          >
                            <div className="flex flex-row gap-2 items-start">
                              {isSelected ? <CircleCheck className="fill-primary stroke-white h-7 w-7" /> : <Circle />}
                              <div className="bg-green-600 text-[14px] font-semibold h-[30px] w-[30px] text-white flex items-center justify-center rounded-full">
                                <span>{capitalize(candidName[0]?.charAt(0))}{capitalize(candidName[candidName.length - 1]?.charAt(0))}</span>
                              </div>
                              <div className="flex flex-col">
                                <div className="flex flex-row items-start gap-2">
                                  <span className="text-[12px]">{candid?.name || ""}</span>
                                  <span className="text-[10px] bg-amber-100 text-amber-700 px-2 rounded-full">Best Match</span>
                                </div>
                                <div className="flex flex-row items-center gap-2 text-[10px] text-gray-500">
                                  <span>Owner ID: #{candid?.userId}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-row items-start justify-between">
                              <div className="flex flex-row items-start gap-2">
                                {isExactPhone ? <CircleCheck className="stroke-green-600 h-4 w-4"/> : <CircleDashed className="h-4 w-4"/>}
                                <div className="flex flex-col">
                                  <span>Mobile</span>
                                  <span className="text-[12px] text-gray-500">{candid?.phoneNo || ""}</span>
                                </div>
                              </div>
                              <span className={cn("text-[12px]", isExactPhone ? "text-green-600" : "text-gray-400")}>{isExactPhone ? "Exact Match" : "Different"}</span>
                            </div>
                            <div className="flex flex-row items-start justify-between">
                              <div className="flex flex-row items-start gap-2">
                                {isExactEmail ? <CircleCheck className="stroke-green-600 h-4 w-4"/> : <CircleDashed className="h-4 w-4"/>}
                                <div className="flex flex-col">
                                  <span>Email</span>
                                  <span className="text-[12px] text-gray-500">{candid?.email || ""}</span>
                                </div>
                              </div>
                              <span className={cn("text-[12px]", isExactEmail ? "text-green-600" : "text-gray-400")}>{isExactEmail ? "Exact Match" : "Different"}</span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  <div className="lg:col-span-5">
                    {/* <DataTable
                      data={tableData || []}
                      columns={columns}
                      className="shadow-none border-0"
                      hideFooter
                    /> */}
                    <div className="overflow-x-auto w-full mx-5">
                      <div className="min-w-[500px]">
                        <Table>
                          <TableHeader>
                            <TableRow className="mb-3 rounded">
                              <CustomTableHead className="w-[50px] text-start">
                                Field
                              </CustomTableHead>
                              <CustomTableHead className="w-[150px] text-start">
                                From Your File
                              </CustomTableHead>
                              <CustomTableHead className="w-[150px] text-start">
                                Matched Candidate
                              </CustomTableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rowTableData?.map((_: any, index: number) => {                            
                              return (
                                <TableRow
                                  key={`row-${index}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                >
                                  <CustomTableCell>
                                    {_?.field}
                                  </CustomTableCell>
                                  <CustomTableCell className={cn(selectedCandidate ? _.isSame ? "bg-green-50" : "bg-red-50" : "")}>
                                    <div className="flex flex-col gap-2">
                                      <span>{_?.fromYourFile}</span>
                                      <div>
                                        <span className={cn("text-[10px] px-2 p-1 rounded-full", selectedCandidate ? _.isSame ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600" : "" )}>{_?.isSame ? "Exact Match" : "Different"}</span>
                                      </div>
                                    </div>
                                  </CustomTableCell>
                                  <CustomTableCell className={cn(selectedCandidate ? _.isSame ? "bg-green-50" : "bg-red-50" : "")}>
                                    {_?.matchCandid}
                                  </CustomTableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>

      <div className="flex flex-row justify-end item-center gap-2">
        <Button disabled={isUploadPending || !disabledButton} onClick={onImport}>
          {isUploadPending ? <Loader variant="secondary" /> : <></>}
          Apply & Import
        </Button>
      </div>
    </div>
  )
}

export default Step3;